import { camelize } from "humps";
import isEmpty from "lodash/isEmpty";
import { AvailabilityWarningIcon } from "PFApp/components/availability_warning_icon";
import { ProfileCustomValuesList } from "PFApp/components/custom_values_list";
import { Icon } from "PFComponents/icon";
import { Markdown } from "PFComponents/markdown";
import { Pill } from "PFComponents/pill";
import { MatchAccuracyBadge } from "PFComponents/resource_metrics/match_accuracy";
import { getMainPosition } from "PFCore/helpers/profile";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { Activity, CustomType, Profile, Shortlist, ShortlistProfile } from "PFTypes";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../compare_details_page.module.scss";

const SHORT_BIO_LENGTH_LIMIT = 150;

export type Row = Pick<CustomType, "name" | "display_as" | "kind"> & { id: number | string };
export type ComparableProfile = Omit<Shortlist, "profile"> & {
  profile: Profile & ShortlistProfile;
  availabilityMatch: number;
  isAvailable: boolean;
  scores?: {
    normalized_score?: number | null;
    normalizedScore?: number | null;
  };
};

type GetCellDataReturn = {
  profile: Profile;
  mobileName: React.ReactElement;
  node: React.ReactElement;
};

export const useCellData = (activity: Activity) => {
  const { customTypes } = useCustomTypes();
  const { t } = useTranslation("activities");

  const [recalcOffsetCounter, setRecalcOffsetCounter] = useState(1);

  const updateOffsets = useCallback(
    () => setRecalcOffsetCounter(recalcOffsetCounter + 1),
    [recalcOffsetCounter]
  );

  const getCellData = useCallback(
    (row: Row, comparableProfile: ComparableProfile): GetCellDataReturn => {
      let node;
      const { profile, applicableCustomValues, comparableCustomValues } = comparableProfile;

      const getValues = (name: string) =>
        profile[camelize(name)] || getMainPosition(profile)[camelize(name)] || [];

      if (row.id === "bio") {
        const summary = (profile?.summary || "").trim();
        node = !summary ? (
          "-"
        ) : (
          <Markdown
            raw={summary}
            crop={SHORT_BIO_LENGTH_LIMIT}
            emojify={false}
            onToggle={updateOffsets}
            className={css.shortBio}
          />
        );
      } else if (row.id === "availability") {
        const availabilityScore = comparableProfile?.availabilityMatch;
        const normalizedScore = comparableProfile.scores?.normalized_score;
        // if activity requires availability, and score is null or undefined
        // it means that the profile is not a match (so it lacks the score)
        // eslint-disable-next-line eqeqeq
        if (activity.metadata?.availability && availabilityScore == null && normalizedScore == null) {
          node = <Pill text={t("compare.notAMatch")} style={{ margin: "20px 0" }} />;
        } else {
          const isFilledAndAvailable = comparableProfile?.state === "filled" && comparableProfile.isAvailable;
          node =
            // null and undefined are both options
            // eslint-disable-next-line eqeqeq
            availabilityScore != null && comparableProfile?.state !== "filled" ? (
              <MatchAccuracyBadge
                style={{ margin: "20px 0" }}
                value={
                  // Conversion to have the range in [0, 1]
                  Math.min(availabilityScore, 1)
                }
                icon={availabilityScore <= 0 ? <AvailabilityWarningIcon /> : undefined}
              />
            ) : (
              <Icon
                style={{ margin: "20px 0" }}
                name={isFilledAndAvailable ? "check" : "cross"}
                size="lg"
                color={isFilledAndAvailable ? "palettePrimaryAction" : "paletteRed0"}
              />
            );
        }
      } else if (row.kind === "multiple") {
        const customType = customTypes.find(({ name }) => row.name === name);
        const fields = activity.custom_fields;
        const field = row.name && fields && fields.find(({ type: { name } }) => name === row.name);

        const filteredComparableCustomValues =
          customType && field && !isEmpty(field.values)
            ? (comparableCustomValues || []).filter(
                ({ id }) =>
                  field.values.some(({ id: valueId }) => valueId === id) && field.type.id === customType.id
              )
            : comparableCustomValues || [];

        const applicables =
          customType && field && !isEmpty(field.values)
            ? [...(applicableCustomValues || []), ...filteredComparableCustomValues].filter(
                ({ customTypeId }) => customTypeId === customType.id
              )
            : getValues(row.name);

        if (applicables.length === 0) {
          node = "-";
        } else {
          node = (
            <ProfileCustomValuesList
              customFieldName={row.name}
              customValues={applicables}
              profileId={profile.id}
              limit={row.name === "skills" ? 5 : 3}
              onMoreLessButtonClick={updateOffsets}
            />
          );
        }
      } else {
        let val = getValues(row.name);
        val = Array.isArray(val) ? val[0] : val;
        node = val ? <div style={{ padding: "3px 0" }}>{val.text}</div> : "";
      }

      return {
        node,
        mobileName: <small className={css.mobileName}>{row.display_as}</small>,
        profile
      };
    },
    [customTypes, t, activity.custom_fields, activity.metadata?.availability, updateOffsets]
  );

  return { getCellData, recalcOffsetCounter };
};
